/**
 * Playout monitor — the widget's EARS-side truth for voice pacing
 * (presenter rails 1.1).
 *
 * THE PROBLEM (found live 2026-08-26, David: "endless, repeats, loses
 * context"): the bot can only observe TTS GENERATION edges —
 * TTSStoppedFrame fires when Cartesia finishes generating, seconds
 * before the audio finishes PLAYING in the visitor's browser (and with
 * Simli in the chain the transport-level bot-stopped-speaking never
 * fires at all: the avatar streams continuous audio, silence included).
 * The flow tick currently schedules off an ESTIMATE (characters sent to
 * TTS at ~14 chars/sec). Estimates drift; drift is exactly how turns
 * pile up or the demo drags.
 *
 * THE FIX: the widget is the only party that actually HEARS playout.
 * This monitor watches the bot's audio level (an AnalyserNode on the
 * remote track — see daily-session.ts) plus the generation edges the
 * bot already sends (`showpilot:bot-speaking`), and fires ONE
 * playout-done per bot turn when:
 *
 *   generation is done  AND  we actually heard audio this episode
 *   AND the line has been silent for `silenceMs`.
 *
 * daily-session forwards that as a `showpilot:playout-done` Daily app
 * message; the bot's flow observer treats it as truth and demotes the
 * chars/sec estimate to a watchdog fallback.
 *
 * Failure honesty: if autoplay is blocked or WebAudio can't run, we
 * never hear audio, so we never fire — the bot's estimate fallback
 * keeps the demo flowing exactly as before this slice. A false
 * mid-turn fire (short gap between TTS segments) is harmless bot-side:
 * the next TTSStartedFrame cancels the tick it scheduled.
 *
 * Pure state machine — clocks and levels are injected, so the whole
 * contract is unit-testable without WebAudio.
 */

/** Sustained-silence window before we call the turn played out. */
export const PLAYOUT_SILENCE_MS = 600;

/**
 * AUDIBLE EDGE (2026-09-04): the one-voice law (media floor, tour-video
 * duck) used to follow the bot's generation edges — and with an avatar
 * vendor in the chain the room keeps playing the voice for seconds after
 * generation ends, so the video came back up mid-sentence ("seems to be
 * on a timer, sometimes they play at the same time"). The monitor now
 * also owns an AUDIBLE signal: on at the generation start, off when the
 * played-out audio actually falls silent (the same silence window as the
 * ack), or immediately at the generation end when this call has never
 * produced a level (no ears → the old behavior, honestly). If the line
 * never goes quiet after generation (a vendor that streams noise), the
 * tail is capped so media never stays paused for good.
 */
export const AUDIBLE_TAIL_MAX_MS = 6000;

/**
 * RMS level (0..1) above which the line counts as audible. Remote
 * speech RMS sits well above this; comfort noise / encoder silence
 * sits well below.
 */
export const PLAYOUT_LEVEL_THRESHOLD = 0.02;

export interface PlayoutMonitor {
  /** Generation edge from `showpilot:bot-speaking {on}` app messages. */
  speaking(on: boolean, now: number): void;
  /** Barge / TTS cancel (`pipecat:interrupted`) — episode void, no ack. */
  interrupted(): void;
  /** Periodic audio level sample (0..1 RMS) from the analyser. */
  sample(level: number, now: number): void;
}

export function createPlayoutMonitor(opts: {
  onPlayoutDone: () => void;
  /** The bot's voice is audible in the room (see AUDIBLE_TAIL_MAX_MS). */
  onAudible?: (on: boolean) => void;
  silenceMs?: number;
  threshold?: number;
  tailMaxMs?: number;
}): PlayoutMonitor {
  const silenceMs = opts.silenceMs ?? PLAYOUT_SILENCE_MS;
  const threshold = opts.threshold ?? PLAYOUT_LEVEL_THRESHOLD;
  const tailMaxMs = opts.tailMaxMs ?? AUDIBLE_TAIL_MAX_MS;

  /** A bot turn is on the wire (speaking(true) seen, not yet acked). */
  let episodeActive = false;
  /** Generation finished for the current episode (speaking(false)). */
  let genDone = false;
  /**
   * We have heard real audio at least once this CALL — the proof that
   * autoplay + WebAudio are alive. Deliberately sticky across episodes:
   * a very short utterance can fall between level samples, and a call
   * whose pipe has already proven itself should still ack it off the
   * silence window. Never true ⇒ never ack ⇒ the bot's estimate
   * fallback owns pacing (the honest degrade).
   */
  let heardAudio = false;
  /** Start of the current silent stretch, null while audible. */
  let silenceSince: number | null = null;
  /** When generation ended for this episode (tail cap anchor). */
  let genDoneAt = 0;
  /** What we last told the audible listener. */
  let audible = false;

  const setAudible = (on: boolean): void => {
    if (audible === on) return;
    audible = on;
    opts.onAudible?.(on);
  };

  return {
    speaking(on: boolean, now: number) {
      if (on) {
        // New utterance (turn start, or the next TTS segment of the
        // same turn). Either way: an ack is owed again and any pending
        // silence stretch is void.
        episodeActive = true;
        genDone = false;
        silenceSince = null;
        setAudible(true);
        return;
      }
      if (episodeActive) {
        genDone = true;
        genDoneAt = now;
      }
      // No ears this call (autoplay blocked, WebAudio down): the
      // generation edge is the only truth we have — fall off with it.
      if (!heardAudio) setAudible(false);
    },

    interrupted() {
      // The viewer barged — the turn never finished playing on its own
      // terms, so no playout-done. The bot handles barge separately.
      episodeActive = false;
      genDone = false;
      silenceSince = null;
      setAudible(false);
    },

    sample(level: number, now: number) {
      if (!episodeActive) return;
      if (level >= threshold) {
        heardAudio = true;
        silenceSince = null;
        // A vendor that never goes quiet must not pin media paused.
        if (genDone && now - genDoneAt >= tailMaxMs) setAudible(false);
        return;
      }
      if (!genDone || !heardAudio) return;
      if (silenceSince === null) {
        silenceSince = now;
        return;
      }
      if (now - silenceSince >= silenceMs) {
        episodeActive = false;
        genDone = false;
        silenceSince = null;
        setAudible(false);
        opts.onPlayoutDone();
      }
    },
  };
}
